import React, { useEffect, lazy, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SkipLink, PageLoader, ErrorBoundary } from '@vastra/ui';
import { StoreProvider, useCatalog, useCart, useAuth, useWishlist } from './context';
import CustomerLayout from './components/CustomerLayout';
import { CookieConsent } from './components/CookieConsent';
import ProductGroup from './pages/customer/ProductGroup';
import { trackPageView, initAutoClickTracking } from './lib/analytics';
import { api } from './services/api';
import { initI18n, reloadI18n, startI18nPolling } from './lib/i18n';
import { subscribeI18n } from '@vastra/i18n';
import { readLocale, subscribeLocale, configureAdditionalStorefrontLocales } from './lib/locale';
import { clearPageSeo, setPageSeo } from './lib/seo';
import { SITE_CONFIG } from '@vastra/constants';

/** Paths that should not appear in search results. */
function isPrivateSeoPath(pathname: string): boolean {
  const prefixes = ['/cart', '/checkout', '/login', '/profile', '/my-orders', '/wishlist', '/auth'];
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** Allow ProductGroup to mount before catalog loads so /products/group/... → /products?… redirect is immediate. */
function isProductGroupSeoPath(pathname: string): boolean {
  return /^\/products\/group\/[^/]+\/[^/]+\/?$/.test(pathname);
}

function isAuthCallbackPath(pathname: string): boolean {
  return pathname === '/auth/callback';
}

const Home = lazy(() => import('./pages/customer/Home'));
const ProductListing = lazy(() => import('./pages/customer/ProductListing'));
const ProductDetails = lazy(() => import('./pages/customer/ProductDetails'));
const Cart = lazy(() => import('./pages/customer/Cart'));
const Checkout = lazy(() => import('./pages/customer/Checkout'));
const CheckoutComplete = lazy(() => import('./pages/customer/CheckoutComplete'));
const OrderStatus = lazy(() => import('./pages/customer/OrderStatus'));
const BlogListing = lazy(() => import('./pages/customer/BlogListing'));
const BlogDetails = lazy(() => import('./pages/customer/BlogDetails'));
const MyOrders = lazy(() => import('./pages/customer/MyOrders'));
const OrderDetail = lazy(() => import('./pages/customer/OrderDetail'));
const ReturnExchange = lazy(() => import('./pages/customer/ReturnExchange'));
const Profile = lazy(() => import('./pages/customer/Profile'));
const ProfileAddressPage = lazy(() => import('./pages/customer/ProfileAddressPage'));
const Login = lazy(() => import('./pages/customer/Login'));
const AuthCallback = lazy(() => import('./pages/customer/AuthCallback'));
const Wishlist = lazy(() => import('./pages/customer/Wishlist'));
const Sitemap = lazy(() => import('./pages/customer/Sitemap'));
const CouponEligible = lazy(() => import('./pages/customer/CouponEligible'));
const NotFound = lazy(() => import('./pages/customer/NotFound'));
const ShippingReturns = lazy(() => import('./pages/customer/StaticPages').then((m) => ({ default: m.ShippingReturns })));
const TermsConditions = lazy(() => import('./pages/customer/StaticPages').then((m) => ({ default: m.TermsConditions })));
const PrivacyPolicy = lazy(() => import('./pages/customer/StaticPages').then((m) => ({ default: m.PrivacyPolicy })));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const { pathname } = useLocation();
  const location = useLocation();
  const { loading, facets } = useCatalog();
  const { cartCount } = useCart();
  const auth = useAuth();
  const { wishlistCount } = useWishlist();
  const [, forceI18nRefresh] = useState(0);
  const [multiLanguageEnabled, setMultiLanguageEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api.getSiteSettings().then((s) => {
      if (cancelled) return;
      setMultiLanguageEnabled(s.multiLanguageEnabled !== false);
      configureAdditionalStorefrontLocales(s.additionalStorefrontLocales);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    trackPageView('customer');
  }, [location.pathname, location.search]);

  useEffect(() => {
    const cleanup = initAutoClickTracking('customer');
    return cleanup;
  }, []);

  useEffect(() => {
    if (!isPrivateSeoPath(pathname)) return;
    setPageSeo({
      title: `${SITE_CONFIG.siteName}`,
      description: 'Private account and checkout pages.',
      path: pathname,
      robots: 'noindex, nofollow',
    });
    return () => clearPageSeo();
  }, [pathname]);

  useEffect(() => {
    const locale = readLocale();
    void initI18n('customer', locale);
    const stopPoll = startI18nPolling('customer', locale, 60_000);
    const stopLocale = subscribeLocale((next) => {
      if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.lang = next;
      }
      void reloadI18n('customer', next);
    });
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = locale;
    }
    return () => {
      stopPoll();
      stopLocale();
    };
  }, []);

  useEffect(() => subscribeI18n(() => forceI18nRefresh((n) => n + 1)), []);

  if (loading && !isProductGroupSeoPath(pathname) && !isAuthCallbackPath(pathname)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50">
        <PageLoader label="Loading store" minHeight="100vh" />
      </div>
    );
  }

  const layoutProps = {
    cartCount,
    wishlistCount,
    isAuthenticated: auth.isAuthenticated,
    userName: auth.user?.name,
    userRole: auth.user?.role,
    onLogout: auth.logout,
    categories: facets.categories.filter((c: string) => c !== 'All'),
    tags: facets.tags,
    multiLanguageEnabled,
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface min-w-0">
      <SkipLink />
      <ScrollToTop />
      <ErrorBoundary>
      <Routes>
        {/*
          Top-level route: no CustomerLayout/Suspense so /products/group/... → /products?…
          runs immediately (SEO URLs; avoids lazy Outlet timing).
        */}
        <Route path="/products/group/:type/:value" element={<ProductGroup />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route element={<CustomerLayout {...layoutProps} />}>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<ProductListing />} />
          <Route path="/product/:id" element={<ProductDetails />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/complete" element={<CheckoutComplete />} />
          <Route path="/order-status" element={<OrderStatus />} />
          <Route path="/coupon/:code" element={<CouponEligible />} />
          <Route path="/blog" element={<BlogListing />} />
          <Route path="/blog/:slug" element={<BlogDetails />} />
          <Route path="/my-orders/:orderId/return" element={<ReturnExchange />} />
          <Route path="/my-orders/:orderId" element={<OrderDetail />} />
          <Route path="/my-orders" element={<MyOrders />} />
          {/* Single param route so useParams().addressId is "new" for add; a literal /new route would leave addressId undefined. */}
          <Route path="/profile/address/:addressId" element={<ProfileAddressPage />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/login" element={<Login />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/shipping" element={<ShippingReturns />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>
      </Routes>
      </ErrorBoundary>
      <CookieConsent />
    </div>
  );
}

const App: React.FC = () => (
  <StoreProvider>
    <AppContent />
  </StoreProvider>
);

export default App;
